import {
  APIProvider,
  Map,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { useEffect, useState, useRef } from "react";
import { getInfractionCoordinates } from "../api/infractions/mapRequest";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Content } from "antd/es/layout/layout";
import FilterBar from "../components/FilterBar";
import { Card } from "antd";

function Markers({ infractions, warnings, isClustering, type }) {
  const markerLib = useMapsLibrary("marker");
  const map = useMap();
  const clustererRef = useRef(null);
  const markerObjectsRef = useRef([]);

  useEffect(() => {
    if (!map || !markerLib || (!infractions.length && !warnings.length)) return;

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }
    markerObjectsRef.current.forEach((marker) => marker.setMap(null));
    markerObjectsRef.current = [];

    // Only create infraction markers if type is empty or specifically 'infraction'
    const infractionMarkers =
      type === undefined || type === "infraction"
        ? infractions.map((infraction) => {
            const position = {
              lat: parseFloat(infraction.latitude),
              lng: parseFloat(infraction.longitude),
            };

            const marker = new markerLib.Marker({
              position,
              map: isClustering ? null : map,
              icon: {
                path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
                fillColor: getInfractionMarkerColor(infraction.status),
                fillOpacity: 1,
                strokeColor: getInfractionMarkerBorderColor(infraction.status),
                strokeWeight: 2,
                scale: 2,
                anchor: new window.google.maps.Point(12, 22),
              },
            });

            marker.addListener("click", () => {
              window.open(`/infraction/${infraction.id}`);
            });

            return marker;
          })
        : [];

    // Only create warning markers if type is empty or specifically 'warning'
    const warningMarkers =
      type === undefined || type === "warning"
        ? warnings.map((warning) => {
            const position = {
              lat: parseFloat(warning.latitude),
              lng: parseFloat(warning.longitude),
            };

            const marker = new markerLib.Marker({
              position,
              map: isClustering ? null : map,
              icon: {
                path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
                fillColor: getWarningMarkerColor(warning.status),
                fillOpacity: 1,
                strokeColor: getWarningMarkerBorderColor(warning.status),
                strokeWeight: 2,
                scale: 1.4,
                anchor: new window.google.maps.Point(12, 22),
              },
            });

            marker.addListener("click", () => {
              window.open(`/warning/${warning.id}`);
            });

            return marker;
          })
        : [];

    markerObjectsRef.current = [...infractionMarkers, ...warningMarkers];

    if (isClustering) {
      clustererRef.current = new MarkerClusterer({ map });
      clustererRef.current.addMarkers(markerObjectsRef.current);
    }

    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }
      markerObjectsRef.current.forEach((marker) => marker.setMap(null));
      markerObjectsRef.current = [];
    };
  }, [map, markerLib, infractions, warnings, isClustering, type]);

  return null;
}

function getInfractionMarkerColor(status) {
  switch (status) {
    case "pending":
      return "#FFA500"; // Orange
    case "paid":
      return "#4CAF50"; // Green
    case "canceled":
      return "#FF0000"; // Red
    default:
      return "#808080"; // Grey
  }
}

function getInfractionMarkerBorderColor(status) {
  switch (status) {
    case "pending":
      return "#FF8C00"; // Darker Orange
    case "paid":
      return "#45a049"; // Darker Green
    case "canceled":
      return "#B22222"; // Darker Red
    default:
      return "#696969"; // Darker Grey
  }
}

function getWarningMarkerColor(status) {
  switch (status) {
    case "pending":
      return "#FFD700"; // Gold
    case "finalized":
      return "#0000FF"; // Blue
    case "canceled":
      return "#A52A2A"; // Orange Red
    default:
      return "#808080"; // Grey
  }
}

function getWarningMarkerBorderColor(status) {
  switch (status) {
    case "pending":
      return "#DAA520"; // Golden Rod
    case "finalized":
      return "#00008B"; // Dark Blue
    case "canceled":
      return "#A52A2A"; // Fire Brick
    default:
      return "#696969"; // Darker Grey
  }
}

export default function Maps() {
  const [infractions, setInfractions] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [isClustering, setIsClustering] = useState(true);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState(undefined);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getInfractionCoordinates({
        start_date: dateRange
          ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
          : null,
        end_date: dateRange ? dateRange[1].format("YYYY-MM-DD HH:mm:ss") : null,
      });
      setInfractions(response.infractions || []);
      setWarnings(response.warnings || []);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const mapStyles = [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ];

  return (
    <div>
      <Content className="py-4 sm:px-6 lg:px-8 h-[15vh]">
        <FilterBar
          dateRange={dateRange}
          onDateChange={setDateRange}
          onSearch={fetchData}
          loading={loading}
          isClustering={isClustering}
          onClusteringChange={setIsClustering}
          type={type}
          setType={setType}
        />
      </Content>

      <APIProvider
        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={["marker"]}
        region="MA"
      >
        <Map
          style={{ width: "100%", height: "85vh" }}
          defaultCenter={{ lat: 35.688469, lng: -5.688669 }}
          defaultZoom={11}
          gestureHandling={"greedy"}
          disableDefaultUI={false}
          options={{
            mapTypeControl: true, // Keeps map type control
            zoomControl: true, // Keeps zoom control
            streetViewControl: false, // Hides the Street View button
          }}
          styles={mapStyles}
        >
          <Markers
            infractions={infractions}
            warnings={warnings}
            type={type}
            isClustering={isClustering}
          />
          <Card
            title="Légende de la carte"
            className="absolute bottom-8 left-4 w-72 z-50 shadow-lg"
            headStyle={{
              padding: "12px 16px",
              fontSize: "16px",
              fontWeight: "600",
              borderBottom: "1px solid #f0f0f0",
            }}
            bodyStyle={{ padding: "10px" }}
          >
            <div className="flex justify-around ">
              {/* Only show warnings legend when type is empty or warning */}
              {(type === undefined || type === "warning") && (
                <div className="">
                  <h3 className="font-medium text-base m-0">Avertissement</h3>
                  <div className="grid gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[#FFD700] text-lg">⬤</span>
                      <span className="text-sm">En attente</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#0000FF] text-lg">⬤</span>
                      <span className="text-sm">Infraction confirmée</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#A52A2A] text-lg">⬤</span>
                      <span className="text-sm">Annulée</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Only show infractions legend when type is empty or infraction */}
              {(type === undefined || type === "infraction") && (
                <div className="">
                  <h3 className="font-medium text-base m-0">Infractions</h3>
                  <div className="grid gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[#FFA500] text-lg">⬤</span>
                      <span className="text-sm">Non payé</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#4CAF50] text-lg">⬤</span>
                      <span className="text-sm">Payée</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#FF0000] text-lg">⬤</span>
                      <span className="text-sm">Annulée</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Map>
      </APIProvider>
    </div>
  );
}
